import { useEffect, useState } from 'react';
import client from '../api/client';

export default function NetworkPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('ALL');
  const [statusTab, setStatusTab] = useState('ACTIVE');
  const [search, setSearch] = useState('');
  const [viewModal, setViewModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editOtp, setEditOtp] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [suspendModal, setSuspendModal] = useState(null); // {rep, action: 'SUSPEND'|'UNSUSPEND'}
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendOtp, setSuspendOtp] = useState('');
  const [suspendOtpSent, setSuspendOtpSent] = useState(false);
  const [suspendLoading, setSuspendLoading] = useState(false);
  // FEATURE-RESET-PIN - admin clears rep's PIN on rep portal + CRM + identity
  const [resetPinModal, setResetPinModal] = useState(null);  // {rep}
  const [resetPinOtp, setResetPinOtp] = useState('');
  const [resetPinOtpSent, setResetPinOtpSent] = useState(false);
  const [resetPinLoading, setResetPinLoading] = useState(false);
  const [viewType, setViewType] = useState('TABLE');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    Promise.all([
      client.get('/admin/affiliate/reps'),
      client.get('/admin/affiliate/regions'),
      client.get('/admin/affiliate/areas')
    ])
      .then(([repsRes, regionsRes, areasRes]) => {
        setPartners(repsRes.data || []);
        setRegions(regionsRes.data || []);
        setAreas(areasRes.data || []);
      })
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
  }, []);



  const requestSuspendOtp = async () => {
    setSuspendLoading(true);
    try {
      await client.post('/admin/affiliate/reps/' + suspendModal.rep.id + '/suspend/request-otp', {
        action: suspendModal.action, reason: suspendReason
      });
      setSuspendOtpSent(true);
      alert('OTP sent to your admin email.');
    } catch(e) { alert(e.response?.data || 'Failed to send OTP'); }
    finally { setSuspendLoading(false); }
  };

  // FEATURE-RESET-PIN handlers
  const requestResetPinOtp = async () => {
    setResetPinLoading(true);
    try {
      await client.post('/admin/affiliate/reps/' + resetPinModal.rep.id + '/reset-pin/request-otp', {});
      setResetPinOtpSent(true);
    } catch (e) {
      alert('Could not send OTP: ' + (e.response?.data || e.message));
    } finally { setResetPinLoading(false); }
  };

  const confirmResetPin = async () => {
    if (!/^[0-9]{6}$/.test(resetPinOtp)) { alert('Please enter the 6-digit OTP.'); return; }
    setResetPinLoading(true);
    try {
      const res = await client.post('/admin/affiliate/reps/' + resetPinModal.rep.id + '/reset-pin', {
        otp: resetPinOtp
      });
      const d = res.data || {};
      alert('PIN reset for ' + resetPinModal.rep.name + '.\n' +
            'They will be asked to set a new PIN on next sign-in (rep portal + CRM).\n' +
            'A notification email has been sent to them.');
      setResetPinModal(null); setResetPinOtp(''); setResetPinOtpSent(false);
    } catch (e) {
      alert('Reset failed: ' + (e.response?.data || e.message));
    } finally { setResetPinLoading(false); }
  };

  const confirmSuspend = async () => {
    setSuspendLoading(true);
    try {
      const endpoint = suspendModal.action === 'SUSPEND' ? '/suspend' : '/unsuspend';
      await client.post('/admin/affiliate/reps/' + suspendModal.rep.id + endpoint, {
        reason: suspendReason, otp: suspendOtp
      });
      alert(suspendModal.action === 'SUSPEND' ? 'Partner suspended.' : 'Partner unsuspended.');
      setSuspendModal(null); setSuspendReason(''); setSuspendOtp(''); setSuspendOtpSent(false);
      setLoading(true);
      client.get('/admin/affiliate/reps').then(r => setPartners(r.data || [])).catch(() => setPartners([])).finally(() => setLoading(false));
    } catch(e) { alert(e.response?.data || 'Action failed'); }
    finally { setSuspendLoading(false); }
  };

  const requestEditOtp = async () => {
    if (!editModal) return;
    setEditLoading(true);
    try {
      await client.post('/admin/affiliate/applications/' + editModal.id + '/request-otp', { action: 'EDIT_PARTNER' });
      alert('OTP sent to your admin email.');
    } catch(e) {
      alert(e.response?.data || 'Failed to send OTP');
    } finally {
      setEditLoading(false);
    }
  };

  const saveEdit = async () => {
    if (!editModal) return;
    try {
      await client.put('/admin/affiliate/reps/' + editModal.id, {
        phone: editModal.phone || '',
        roleApplied: editModal.roleApplied || editModal.role_applied || 'SALES_PARTNER',
        regionId: editModal.regionId || editModal.region_id || '',
        areaId: editModal.areaId || editModal.area_id || '',
        isActive: String(editModal.isActive ?? editModal.is_active ?? true),
        reason: editModal.reason || '',
        otp: editOtp
      });
      setEditModal(null);
      setEditOtp('');
      setLoading(true);
      Promise.all([
        client.get('/admin/affiliate/reps'),
        client.get('/admin/affiliate/regions'),
        client.get('/admin/affiliate/areas')
      ])
        .then(([repsRes, regionsRes, areasRes]) => {
          setPartners(repsRes.data || []);
          setRegions(regionsRes.data || []);
          setAreas(areasRes.data || []);
        })
        .catch(() => setPartners([]))
        .finally(() => setLoading(false));
    } catch(e) {
      alert(e.response?.data || 'Failed to update partner');
    }
  };

  const roleLabel = (role) => {
    if (role === 'REGIONAL_MANAGER') return 'Regional Manager';
    if (role === 'AREA_MANAGER') return 'Area Manager';
    return 'Sales Rep';
  };

  const roleOf = (p) => p.roleApplied || p.role_applied || 'SALES_PARTNER';
  const q = search.toLowerCase().trim();

  const filteredPartners = partners.filter(p => {
    const role = roleOf(p);
    const matchesTab = tab === 'ALL' || role === tab;
    const isActive = p.isActive ?? p.is_active;
    const repStatus = p.status || (isActive ? 'ACTIVE' : 'INACTIVE');
    const matchesStatus =
      statusTab === 'ALL' ||
      (statusTab === 'ACTIVE' && repStatus === 'ACTIVE') ||
      (statusTab === 'SUSPENDED' && repStatus === 'SUSPENDED');
    const matchesSearch = !q ||
      (p.name || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.phone || '').toLowerCase().includes(q) ||
      (p.refCode || p.ref_code || '').toLowerCase().includes(q);
    return matchesTab && matchesStatus && matchesSearch;
  });


  const idOf = (v) => v || '';
  const regionIdOf = (p) => p.regionId || p.region_id || '';
  const areaIdOf = (p) => p.areaId || p.area_id || '';

  const hierarchyData = regions.map(region => {
    const rid = idOf(region.id);
    const regionPartners = filteredPartners.filter(p => regionIdOf(p) === rid);

    const regionalManagers = regionPartners.filter(p => roleOf(p) === 'REGIONAL_MANAGER');

    const regionAreas = areas.filter(a => (a.region_id || a.regionId) === rid).map(area => {
      const aid = idOf(area.id);
      const areaPartners = filteredPartners.filter(p => areaIdOf(p) === aid);

      return {
        ...area,
        areaManagers: areaPartners.filter(p => roleOf(p) === 'AREA_MANAGER'),
        salesReps: areaPartners.filter(p => roleOf(p) === 'SALES_PARTNER')
      };
    }).filter(a => a.areaManagers.length > 0 || a.salesReps.length > 0);

    return {
      ...region,
      regionalManagers,
      areaNodes: regionAreas
    };
  }).filter(r => r.regionalManagers.length > 0 || r.areaNodes.length > 0);

  return (
    <div>
      <h1 style={{ color:'#000', fontSize:22, fontWeight:800, margin:'0 0 4px', fontFamily:'Georgia,serif' }}>
        Network Partners
      </h1>
      <p style={{ color:'#555', fontSize:13, margin:'0 0 22px' }}>
        Approved Regional Managers, Area Managers, and Sales Reps
      </p>

      <input
        value={search}
        onChange={e=>setSearch(e.target.value)}
        placeholder="Search name, email, phone, or referral code..."
        style={{ width:'100%', maxWidth:600, padding:'12px 16px', border:'1px solid #ddd', borderRadius:10, fontSize:15, color:'#000', outline:'none', marginBottom:16 }}
      />

      <div style={{ display:'flex', gap:8, marginBottom:20, borderBottom:'1px solid #eee', paddingBottom:12, flexWrap:'wrap' }}>
        {[
          ['ALL','All'],
          ['REGIONAL_MANAGER','Regional Managers'],
          ['AREA_MANAGER','Area Managers'],
          ['SALES_PARTNER','Sales Reps']
        ].map(([key,label]) => (
          <button key={key} onClick={()=>setTab(key)}
            style={{ padding:'8px 16px', background:tab===key?'#1a3cff':'#fff', color:tab===key?'#fff':'#444', border:tab===key?'none':'1px solid #ddd', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
        {[
          ['ACTIVE','Active'],
          ['SUSPENDED','Suspended'],
          ['ALL','All Statuses']
        ].map(([key,label]) => (
          <button key={key} onClick={()=>setStatusTab(key)}
            style={{ padding:'8px 18px', borderRadius:20, border:'1px solid ' + (key==='SUSPENDED' ? '#c0392b' : '#ddd'),
              background: statusTab===key ? (key==='SUSPENDED' ? '#c0392b' : '#1a3cff') : '#fff',
              color: statusTab===key ? '#fff' : (key==='SUSPENDED' ? '#c0392b' : '#444'),
              fontWeight:700, fontSize:13, cursor:'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
        {[
          ['TABLE','Table View'],
          ['HIERARCHY','Hierarchy View']
        ].map(([key,label]) => (
          <button key={key} onClick={()=>setViewType(key)}
            style={{ padding:'8px 16px', background:viewType===key?'#1a3cff':'#fff', color:viewType===key?'#fff':'#444', border:viewType===key?'none':'1px solid #ddd', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {viewType === 'TABLE' && (
      <div style={{ background:'#fff', border:'1px solid #eee', borderRadius:12, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:14 }}>
          <thead>
            <tr style={{ background:'#f9f9f9', borderBottom:'1px solid #eee' }}>
              <th style={{ padding:12, textAlign:'left', color:'#555' }}>Name</th>
              <th style={{ padding:12, textAlign:'left', color:'#555' }}>Email</th>
              <th style={{ padding:12, textAlign:'left', color:'#555' }}>Phone</th>
              <th style={{ padding:12, textAlign:'left', color:'#555' }}>Role</th>
              <th style={{ padding:12, textAlign:'left', color:'#555' }}>Region</th>
              <th style={{ padding:12, textAlign:'left', color:'#555' }}>Area</th>
              {/* FIX-ADMIN-EXT-COL - Extension column */}
              <th style={{ padding:12, textAlign:'left', color:'#555' }}>Extension</th>
              <th style={{ padding:12, textAlign:'left', color:'#555' }}>Referral Code</th>
              <th style={{ padding:12, textAlign:'left', color:'#555' }}>Status</th>
              <th style={{ padding:12, textAlign:'left', color:'#555' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="10" style={{ padding:30, textAlign:'center', color:'#888' }}>Loading...</td></tr>
            ) : filteredPartners.length === 0 ? (
              <tr><td colSpan="10" style={{ padding:30, textAlign:'center', color:'#888' }}>No network partners found</td></tr>
            ) : filteredPartners.map(p => {
              const role = roleOf(p);
              const active = p.isActive ?? p.is_active;
              const repStatus = p.status || (active ? 'ACTIVE' : 'INACTIVE');
            
  return (
                <tr key={p.id} style={{ borderBottom:'1px solid #f5f5f5' }}>
                  <td style={{ padding:12, color:'#000', fontWeight:700 }}>{p.name || '—'}</td>
                  <td style={{ padding:12, color:'#333' }}>{p.email || '—'}</td>
                  <td style={{ padding:12, color:'#555' }}>{p.phone || '—'}</td>
                  <td style={{ padding:12, color:'#1a3cff', fontWeight:700 }}>{roleLabel(role)}</td>
                  <td style={{ padding:12, color:'#555' }}>{p.region_name ? p.region_name + (p.state_code ? ' (' + p.state_code + ')' : '') : '—'}</td>
                  <td style={{ padding:12, color:'#555' }}>{p.area_name || p.area_city || '—'}</td>
                  <td style={{ padding:12, color:'#1f2937', fontWeight:600 }}>{p.extension_number ? 'x' + p.extension_number : '—'}</td>
                  <td style={{ padding:12, color:'#1a3cff', fontWeight:700 }}>{p.refCode || p.ref_code || '—'}</td>
                  <td style={{ padding:12, color:active ? '#0a8a40' : '#c0392b', fontWeight:700 }}>
                    {active ? 'ACTIVE' : 'INACTIVE'}
                  </td>
                  <td style={{ padding:12 }}>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={()=>setViewModal(p)} style={{ padding:'6px 12px', background:'#eff4ff', color:'#1a3cff', border:'none', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                        View
                      </button>
                      <button onClick={()=>setEditModal(p)} style={{ padding:'6px 12px', background:'#fff8ec', color:'#b45309', border:'none', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                        Edit
                      </button>
                      {/* FEATURE-RESET-PIN button */}
                      <button onClick={()=>{ setResetPinModal({rep:p}); setResetPinOtp(''); setResetPinOtpSent(false); }}
                        style={{ padding:'6px 12px', background:'#f3e8ff', color:'#6b21a8', border:'none', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                        Reset PIN
                      </button>
                      {(p.status || (p.isActive || p.is_active ? 'ACTIVE' : 'INACTIVE')) !== 'SUSPENDED' ? (
                        <button onClick={()=>{ setSuspendModal({rep:p, action:'SUSPEND'}); setSuspendReason(''); setSuspendOtp(''); setSuspendOtpSent(false); }}
                          style={{ padding:'6px 12px', background:'#fff5f5', color:'#c0392b', border:'none', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                          Suspend
                        </button>
                      ) : (
                        <button onClick={()=>{ setSuspendModal({rep:p, action:'UNSUSPEND'}); setSuspendReason(''); setSuspendOtp(''); setSuspendOtpSent(false); }}
                          style={{ padding:'6px 12px', background:'#f0fff6', color:'#0a8a40', border:'none', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                          Unsuspend
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {viewType === 'HIERARCHY' && (
        <div style={{ display:'grid', gap:18 }}>
          {hierarchyData.length === 0 ? (
            <div style={{ background:'#fff', border:'1px solid #eee', borderRadius:12, padding:30, color:'#888', textAlign:'center' }}>No hierarchy data found</div>
          ) : hierarchyData.map(region => (
            <div key={region.id} style={{ background:'#fff', border:'1px solid #eee', borderRadius:12, padding:20 }}>
              <h3 style={{ margin:'0 0 12px', color:'#000' }}>{region.name} {region.state_code ? '(' + region.state_code + ')' : ''}</h3>

              <div style={{ marginBottom:14 }}>
                <div style={{ fontWeight:800, color:'#be185d', marginBottom:6 }}>Regional Managers</div>
                {region.regionalManagers.length === 0 ? <div style={{ color:'#999', fontSize:13 }}>No regional manager assigned</div> : region.regionalManagers.map(p => (
                  <PartnerLine key={p.id} p={p} roleLabel={roleLabel} roleOf={roleOf} setViewModal={setViewModal} setEditModal={setEditModal} />
                ))}
              </div>

              <div style={{ display:'grid', gap:12, marginLeft:20 }}>
                {region.areaNodes.map(area => (
                  <div key={area.id} style={{ border:'1px solid #f0f0f0', borderRadius:10, padding:14, background:'#fafafa' }}>
                    <h4 style={{ margin:'0 0 10px', color:'#111' }}>{area.name}</h4>
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontWeight:800, color:'#5b21b6', marginBottom:6 }}>Area Managers</div>
                      {area.areaManagers.length === 0 ? <div style={{ color:'#999', fontSize:13 }}>No area manager assigned</div> : area.areaManagers.map(p => (
                        <PartnerLine key={p.id} p={p} roleLabel={roleLabel} roleOf={roleOf} setViewModal={setViewModal} setEditModal={setEditModal} />
                      ))}
                    </div>
                    <div style={{ marginLeft:20 }}>
                      <div style={{ fontWeight:800, color:'#1a3cff', marginBottom:6 }}>Sales Reps</div>
                      {area.salesReps.length === 0 ? <div style={{ color:'#999', fontSize:13 }}>No sales reps assigned</div> : area.salesReps.map(p => (
                        <PartnerLine key={p.id} p={p} roleLabel={roleLabel} roleOf={roleOf} setViewModal={setViewModal} setEditModal={setEditModal} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FEATURE-RESET-PIN modal */}
      {resetPinModal && (
        <div style={{ position:'fixed', top:0,left:0,right:0,bottom:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, maxWidth:480, width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ color:'#6b21a8', margin:'0 0 6px' }}>Reset Partner PIN</h3>
            <p style={{ color:'#555', fontSize:13, margin:'0 0 12px' }}>
              This will clear the PIN on <strong>both</strong> the Rep Portal and the CRM. The partner will be asked to set a new 6-digit PIN on their next sign-in (via OTP verification).
            </p>
            <div style={{ padding:'10px 12px', background:'#faf5ff', borderRadius:8, marginBottom:14, fontSize:13 }}>
              <strong>{resetPinModal.rep.name}</strong> &mdash; {resetPinModal.rep.email}
            </div>

            {!resetPinOtpSent ? (
              <>
                <p style={{ fontSize:13, color:'#555', margin:'0 0 14px' }}>Click below to send a verification code to your admin email.</p>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={()=>{ setResetPinModal(null); setResetPinOtp(''); }} disabled={resetPinLoading}
                    style={{ flex:1, padding:'10px 16px', background:'#f3f4f6', color:'#374151', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={requestResetPinOtp} disabled={resetPinLoading}
                    style={{ flex:1, padding:'10px 16px', background:'#6b21a8', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
                    {resetPinLoading ? 'Sending...' : 'Send OTP'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize:13, color:'#0a8a40', margin:'0 0 12px' }}>&#10003; OTP sent. Check your admin email and enter the 6-digit code below.</p>
                <input type="text" inputMode="numeric" maxLength={6} value={resetPinOtp}
                  onChange={e=>setResetPinOtp(e.target.value.replace(/[^0-9]/g,''))}
                  placeholder="6-digit OTP" autoFocus
                  style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #e5e7eb', borderRadius:8, fontSize:18, letterSpacing:8, textAlign:'center', fontWeight:700, marginBottom:14 }} />
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={()=>{ setResetPinModal(null); setResetPinOtp(''); setResetPinOtpSent(false); }} disabled={resetPinLoading}
                    style={{ flex:1, padding:'10px 16px', background:'#f3f4f6', color:'#374151', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={confirmResetPin} disabled={resetPinLoading || resetPinOtp.length !== 6}
                    style={{ flex:1, padding:'10px 16px', background:'#6b21a8', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', opacity:(resetPinLoading || resetPinOtp.length !== 6) ? 0.6 : 1 }}>
                    {resetPinLoading ? 'Resetting...' : 'Confirm Reset'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {viewModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:560, border:'1px solid #eee', maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ color:'#000', margin:'0 0 18px', fontFamily:'Georgia,serif' }}>Network Partner Details</h3>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20, color:'#333', fontSize:14 }}>
              <div><b>Name:</b><br/>{viewModal.name || '—'}</div>
              <div><b>Email:</b><br/>{viewModal.email || '—'}</div>
              <div><b>Phone:</b><br/>{viewModal.phone || '—'}</div>
              <div><b>Role:</b><br/>{roleLabel(roleOf(viewModal))}</div>
              <div><b>Region:</b><br/>{viewModal.region_name ? viewModal.region_name + (viewModal.state_code ? ' (' + viewModal.state_code + ')' : '') : '—'}</div>
              <div><b>Area:</b><br/>{viewModal.area_name || viewModal.area_city || '—'}</div>
              <div><b>Referral Code:</b><br/>{viewModal.refCode || viewModal.ref_code || '—'}</div>
              <div><b>Status:</b><br/>{(viewModal.isActive ?? viewModal.is_active) ? 'ACTIVE' : 'INACTIVE'}</div>
              <div><b>Application ID:</b><br/>{viewModal.applicationId || viewModal.application_id || '—'}</div>
              <div><b>Created:</b><br/>{viewModal.createdAt || viewModal.created_at ? new Date(viewModal.createdAt || viewModal.created_at).toLocaleString() : '—'}</div>
            </div>

            <button onClick={()=>setViewModal(null)}
              style={{ width:'100%', padding:'11px', background:'#1a3cff', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
              Close
            </button>
          </div>
        </div>
      )}

      {suspendModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:460, boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ color: suspendModal.action==='SUSPEND' ? '#c0392b' : '#0a8a40', margin:'0 0 6px' }}>
              {suspendModal.action === 'SUSPEND' ? 'Suspend Partner' : 'Unsuspend Partner'}
            </h3>
            <p style={{ color:'#666', fontSize:13, margin:'0 0 18px' }}>
              {suspendModal.action === 'SUSPEND' ? 'This will block portal access and hold all pending commissions.' : 'This will restore portal access and release held commissions.'}
            </p>
            <div style={{ background:'#f8f9ff', borderRadius:8, padding:'12px 16px', marginBottom:16, fontSize:13 }}>
              <strong>{suspendModal.rep.name}</strong> — {suspendModal.rep.email}
            </div>
            {suspendModal.action === 'SUSPEND' && (
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#555', display:'block', marginBottom:4 }}>REASON *</label>
                <input value={suspendReason} onChange={e=>setSuspendReason(e.target.value)}
                  placeholder="e.g. Compliance review, policy violation..."
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
              </div>
            )}
            <button onClick={requestSuspendOtp} disabled={suspendLoading || (suspendModal.action==='SUSPEND' && !suspendReason.trim())}
              style={{ width:'100%', padding:'10px', background: suspendOtpSent ? '#eee' : '#eff4ff', color: suspendOtpSent ? '#888' : '#1a3cff',
                border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer', marginBottom:12,
                opacity:(suspendModal.action==='SUSPEND' && !suspendReason.trim()) ? 0.5 : 1 }}>
              {suspendOtpSent ? 'Resend OTP' : 'Send OTP to Admin'}
            </button>
            {suspendOtpSent && (
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#555', display:'block', marginBottom:4 }}>OTP CODE *</label>
                <input value={suspendOtp} onChange={e=>setSuspendOtp(e.target.value)} placeholder="Enter OTP"
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
              </div>
            )}
            <div style={{ display:'flex', gap:10, marginTop:8 }}>
              <button onClick={confirmSuspend} disabled={!suspendOtpSent || !suspendOtp || suspendLoading}
                style={{ flex:1, padding:'10px', background: suspendModal.action==='SUSPEND' ? '#c0392b' : '#0a8a40',
                  color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer',
                  opacity:(!suspendOtpSent || !suspendOtp) ? 0.5 : 1 }}>
                {suspendLoading ? 'Processing...' : suspendModal.action === 'SUSPEND' ? 'Suspend Partner' : 'Unsuspend Partner'}
              </button>
              <button onClick={()=>{ setSuspendModal(null); setSuspendReason(''); setSuspendOtp(''); setSuspendOtpSent(false); }}
                style={{ flex:1, padding:'10px', background:'#f5f5f5', color:'#444', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {editModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:560, border:'1px solid #eee', maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ color:'#000', margin:'0 0 18px', fontFamily:'Georgia,serif' }}>Edit Network Partner</h3>

            <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6 }}>Phone</label>
            <input value={editModal.phone || ''} onChange={e=>setEditModal(m=>({...m, phone:e.target.value}))}
              style={{ width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, marginBottom:14, boxSizing:'border-box' }} />

            <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6 }}>Role</label>
            <select value={editModal.roleApplied || editModal.role_applied || 'SALES_PARTNER'} onChange={e=>setEditModal(m=>({...m, roleApplied:e.target.value, role_applied:e.target.value}))}
              style={{ width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, marginBottom:14, boxSizing:'border-box' }}>
              <option value="SALES_PARTNER">Sales Rep</option>
              <option value="AREA_MANAGER">Area Manager</option>
              <option value="REGIONAL_MANAGER">Regional Manager</option>
            </select>

            <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6 }}>Region / State</label>
            <select value={editModal.regionId || editModal.region_id || ''} onChange={e=>setEditModal(m=>({...m, regionId:e.target.value, region_id:e.target.value, areaId:'', area_id:''}))}
              style={{ width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, marginBottom:14, boxSizing:'border-box' }}>
              <option value="">Select region</option>
              {regions.map(r => <option key={r.id} value={r.id}>{r.name} ({r.state_code})</option>)}
            </select>

            {(editModal.roleApplied || editModal.role_applied || 'SALES_PARTNER') !== 'REGIONAL_MANAGER' && (
              <>
                <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6 }}>Area / City</label>
                <select value={editModal.areaId || editModal.area_id || ''} onChange={e=>setEditModal(m=>({...m, areaId:e.target.value, area_id:e.target.value}))}
                  style={{ width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, marginBottom:14, boxSizing:'border-box' }}>
                  <option value="">Select area</option>
                  {areas.filter(a => !((editModal.regionId || editModal.region_id)) || a.region_id === (editModal.regionId || editModal.region_id)).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </>
            )}

            <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6 }}>Status</label>
            <select value={String(editModal.isActive ?? editModal.is_active ?? true)} onChange={e=>setEditModal(m=>({...m, isActive:e.target.value === 'true', is_active:e.target.value === 'true'}))}
              style={{ width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, marginBottom:20, boxSizing:'border-box' }}>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6 }}>Reason for Change *</label>
            <textarea value={editModal.reason || ''} onChange={e=>setEditModal(m=>({...m, reason:e.target.value}))}
              placeholder="Example: Promoted to Area Manager, reassigned territory, corrected phone number..."
              style={{ width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, marginBottom:14, boxSizing:'border-box', height:80, resize:'vertical' }} />

            <button onClick={requestEditOtp} disabled={editLoading}
              style={{ width:'100%', padding:'10px', background:'#eff4ff', color:'#1a3cff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:editLoading?'not-allowed':'pointer', marginBottom:14, opacity:editLoading?0.6:1 }}>
              {editLoading ? 'Sending OTP...' : 'Send OTP'}
            </button>

            <label style={{ display:'block', fontSize:12, fontWeight:700, marginBottom:6 }}>OTP Code *</label>
            <input value={editOtp} onChange={e=>setEditOtp(e.target.value)}
              placeholder="Enter OTP"
              style={{ width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, marginBottom:20, boxSizing:'border-box' }} />

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={saveEdit}
                style={{ flex:1, padding:'11px', background:'#0a8a40', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Save Changes
              </button>
              <button onClick={()=>setEditModal(null)}
                style={{ flex:1, padding:'11px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


function PartnerLine({ p, roleLabel, roleOf, setViewModal, setEditModal }) {
  const active = p.isActive ?? p.is_active;
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, background:'#fff', border:'1px solid #eee', borderRadius:8, padding:'8px 10px', marginBottom:6 }}>
      <div>
        <div style={{ color:'#000', fontWeight:800 }}>{p.name || '—'} <span style={{ color:active?'#0a8a40':'#c0392b', fontSize:11 }}>({active?'ACTIVE':'INACTIVE'})</span></div>
        <div style={{ color:'#666', fontSize:12 }}>{roleLabel(roleOf(p))} • {p.email || '—'} • Ref: {p.refCode || p.ref_code || '—'}</div>
      </div>
      <div style={{ display:'flex', gap:6 }}>
        <button onClick={()=>setViewModal(p)} style={{ padding:'5px 10px', background:'#eff4ff', color:'#1a3cff', border:'none', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer' }}>View</button>
        <button onClick={()=>setEditModal(p)} style={{ padding:'5px 10px', background:'#fff8ec', color:'#b45309', border:'none', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer' }}>Edit</button>
      </div>
    </div>
  );
}
